import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useQueries } from "@tanstack/react-query";
import IngredientTable, { IngredientTableLeftHeader } from "./IngredientTable";
import BatchSummary, { BatchSummaryLeftHeader } from "./BatchSummary";
import StandardOperatingProcedure, { StandardOperatingProcedureLeftHeader } from "./StandardOperatingProcedure";
import SensoryFeedback, { SensoryFeedbackLeftHeader } from "./SensoryFeedback";
import AddBFFProductModal from "./AddBFFProductModal";
import AddStandardIngredientModal from "./AddStandardIngredientModal";
import EditIngredientModal from "./EditIngredientModal";
import ArchiveIngredientModal from "./ArchiveIngredientModal";
import { useDeleteRecipeIngredient } from "@/hooks/mutations/useRecipeMutations";
import { buildIngredientsDisplayData, isConfectionaryRecipe, isBeveragePsdRecipe, getIngredientMatchKey, isIngredientMatch } from "../data/ingredientsCalculations";
import ScaleBatchColumn from "./ScaleBatchColumn";
import { useRecipeById } from "@/hooks/useRecipes";
import api from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { cn } from "@/lib/utils";

// ================= INDEPENDENT VERSION COLUMN COMPONENT =================
// Each VersionColumn coordinates its 4 sub-sections: IngredientTable, BatchSummary, SOP, SensoryFeedback
function VersionColumn({
  vItem,
  data,
  isFirstVersion = false,
  ingredientRef,
  ingredientHeight,
  batchRef,
  sopRef,
  sensoryRef,
  batchHeight,
  sopHeight,
  sensoryHeight,
  globalIngredients = [],
  showSolidLiquidColumn = true,
  formatDate,
  onFinalizeVersion,
  onFullDownload,
  onClientDownload,
  onPrepareSample,
  onSample,
  onEditRow,
  onClearVersionIngredients,
  onSaveSpecificFields,
  recipeFormat = "bakery",
  isConfectionary = false,
  isFinalized = false,
  isSelectingForCompare = false,
  isSelectedForCompare = false,
  onToggleSelectCompare,
  scaleBatchSize,
}) {
  const versionNumStr = String(Number(vItem?.version ?? 0) + 1).padStart(2, "0");
  const isCurrentData =
    vItem?._id === data?._id ||
    (vItem?.version !== undefined && data?.version !== undefined && vItem?.version === data?.version);
  const targetId = vItem?._id;

  const { data: fetchedDetail } = useRecipeById(targetId, {
    enabled: !isCurrentData && !!targetId && (!Array.isArray(vItem?.ingredients) || vItem.ingredients.length === 0),
  });

  const effectiveVersionData = useMemo(() => {
    if (isCurrentData && data) return data;
    if (fetchedDetail) return { ...vItem, ...fetchedDetail };
    return vItem || {};
  }, [isCurrentData, data, fetchedDetail, vItem]);

  const effectiveStatus = effectiveVersionData?.recipeStatus || vItem?.recipeStatus;
  const vIsFinalized =
    ["final", "approved", "finalized"].includes(String(effectiveStatus || "").toLowerCase()) ||
    Boolean(
      isCurrentData &&
      (["final", "approved", "finalized"].includes(String(data?.recipeStatus || "").toLowerCase()) || isFinalized)
    );

  // Ensure version ingredients are sourced properly from vItem or fetched details
  const versionIngredients = useMemo(() => {
    if (Array.isArray(effectiveVersionData?.ingredients) && effectiveVersionData.ingredients.length > 0) {
      return effectiveVersionData.ingredients;
    }
    if (isCurrentData && Array.isArray(data?.ingredients) && data.ingredients.length > 0) {
      return data.ingredients;
    }
    return [];
  }, [effectiveVersionData?.ingredients, isCurrentData, data?.ingredients]);

  const normalizedVItem = useMemo(
    () => ({
      ...effectiveVersionData,
      recipeType: effectiveVersionData?.recipeType || data?.recipeType,
      ingredients: versionIngredients,
    }),
    [data?.recipeType, effectiveVersionData, versionIngredients]
  );

  // Batch Summary Independent Edit Mode states for THIS version
  const [isBatchSummaryEditing, setIsBatchSummaryEditing] = useState(false);
  const [draftYield, setDraftYield] = useState(
    effectiveVersionData?.yield ?? effectiveVersionData?.outputYield ?? data?.yield ?? data?.outputYield ?? 100
  );
  const [draftServingSize, setDraftServingSize] = useState(
    effectiveVersionData?.servingSize ?? effectiveVersionData?.outputServingSize ?? data?.servingSize ?? data?.outputServingSize ?? 100
  );
  const [draftPerPiece, setDraftPerPiece] = useState(
    effectiveVersionData?.perPiece ?? vItem?.perPiece ?? data?.perPiece ?? 0
  );
  const [draftPacketQuantity, setDraftPacketQuantity] = useState(
    effectiveVersionData?.packetQuantity ?? vItem?.packetQuantity ?? data?.packetQuantity ?? 0
  );

  useEffect(() => {
    setDraftYield(
      effectiveVersionData?.yield ?? effectiveVersionData?.outputYield ?? data?.yield ?? data?.outputYield ?? 100
    );
    setDraftServingSize(
      effectiveVersionData?.servingSize ?? effectiveVersionData?.outputServingSize ?? data?.servingSize ?? data?.outputServingSize ?? 100
    );
    setDraftPerPiece(
      effectiveVersionData?.perPiece ?? vItem?.perPiece ?? data?.perPiece ?? 0
    );
    setDraftPacketQuantity(
      effectiveVersionData?.packetQuantity ?? vItem?.packetQuantity ?? data?.packetQuantity ?? 0
    );
  }, [
    effectiveVersionData?.yield,
    effectiveVersionData?.outputYield,
    data?.yield,
    data?.outputYield,
    effectiveVersionData?.servingSize,
    effectiveVersionData?.outputServingSize,
    data?.servingSize,
    data?.outputServingSize,
    effectiveVersionData?.perPiece,
    vItem?.perPiece,
    data?.perPiece,
    effectiveVersionData?.packetQuantity,
    vItem?.packetQuantity,
    data?.packetQuantity,
  ]);

  // Compute calculated metrics dynamically for this specific version
  const vComputedData = useMemo(
    () =>
      buildIngredientsDisplayData(normalizedVItem, {
        recipeFormat: recipeFormat || data?.recipeType,
        yield: draftYield,
        outputYield: draftYield,
        servingSize: draftServingSize,
        outputServingSize: draftServingSize,
        perPiece: draftPerPiece,
        packetQuantity: draftPacketQuantity,
      }),
    [normalizedVItem, recipeFormat, data?.recipeType, draftYield, draftServingSize, draftPerPiece, draftPacketQuantity]
  );

  const { ingredients: vIngredients, totals: vTotals, batchSummary: vBatchSummary } = vComputedData;

  const defaultTotalQuantity = useMemo(() => {
    const sum = vIngredients.reduce((acc, curr) => acc + (Number(curr.quantity) || 0), 0);
    return sum > 0 ? sum : (Number(vTotals?.quantity) || 0);
  }, [vIngredients, vTotals?.quantity]);

  const currentBatchSize = Number(scaleBatchSize) || defaultTotalQuantity || 0;
  const scaleFactor = defaultTotalQuantity > 0 && currentBatchSize > 0 && scaleBatchSize !== undefined && scaleBatchSize !== null && scaleBatchSize !== ""
    ? currentBatchSize / defaultTotalQuantity
    : 1;

  const displayIngredients = useMemo(() => {
    if (scaleFactor === 1) return vIngredients;
    return vIngredients.map((item) => {
      const originalQty = Number(item.quantity) || 0;
      const scaledQty = originalQty * scaleFactor;
      const bffRate = Number(item.bffRate ?? item.bffRateAtCreation ?? 0);
      const clientRate = Number(item.clientRate ?? item.clientRateAtCreation ?? 0);
      const scaledBffCost = (bffRate * scaledQty) / 1000;
      const scaledClientCost = (clientRate * scaledQty) / 1000;
      return {
        ...item,
        quantity: scaledQty % 1 === 0 ? String(scaledQty) : scaledQty.toFixed(2),
        displayQuantity: scaledQty % 1 === 0 ? String(scaledQty) : scaledQty.toFixed(2),
        bffRate,
        clientRate,
        bffCost: scaledBffCost,
        clientCost: scaledClientCost,
      };
    });
  }, [vIngredients, scaleFactor]);

  const effectiveTotals = useMemo(() => {
    if (scaleFactor === 1) return vTotals;
    const totalQtyStr = defaultTotalQuantity > 0 && currentBatchSize > 0
      ? (currentBatchSize % 1 === 0 ? String(currentBatchSize) : currentBatchSize.toFixed(2))
      : (vTotals?.quantity || "0");
    return {
      ...vTotals,
      quantity: totalQtyStr,
      bffCost: Number(vTotals?.bffCost || 0) * scaleFactor,
      clientCost: Number(vTotals?.clientCost || 0) * scaleFactor,
    };
  }, [vTotals, scaleFactor, defaultTotalQuantity, currentBatchSize]);

  const vIngredientsByKey = useMemo(() => {
    const map = new Map();
    (displayIngredients || []).forEach((ing) => {
      const key = getIngredientMatchKey(ing);
      if (key && !map.has(key)) {
        map.set(key, ing);
      }
    });
    return map;
  }, [displayIngredients]);

  // Group ingredients by segment (e.g. liquid group, solid group) so S/L (%) renders as a single merged cell
  const ingredientGroups = useMemo(() => {
    if (!globalIngredients || globalIngredients.length === 0) return [];

    const groups = [];
    let currentGroup = null;

    globalIngredients.forEach((item, index) => {
      const itemKey = getIngredientMatchKey(item);
      let matchedVIng = vIngredientsByKey.get(itemKey);
      if (!matchedVIng) {
        matchedVIng = (displayIngredients || []).find((ing) => isIngredientMatch(ing, item));
      }
      const vIng = matchedVIng || {
        ...item,
        _id: undefined,
        quantity: null,
        displayQuantity: "-",
        composition: "-",
        perSachet: "-",
        finalDosing: "-",
        bffCost: null,
        clientCost: null,
        solidLiquid: null,
        isMissingInVersion: true,
        originalIndex: -1,
      };
      const typeKey = String(item.typeKey || item.type || "").toLowerCase();

      const prevItem = index > 0 ? globalIngredients[index - 1] : null;
      const prevTypeKey = prevItem ? String(prevItem.typeKey || prevItem.type || "").toLowerCase() : "";

      const shouldStartNewGroup =
        !currentGroup ||
        prevItem?.isSeparator ||
        (typeKey !== prevTypeKey &&
          (typeKey === "liquid" ||
            typeKey === "solid" ||
            prevTypeKey === "liquid" ||
            prevTypeKey === "solid"));

      if (shouldStartNewGroup) {
        if (currentGroup) {
          groups.push(currentGroup);
        }
        currentGroup = {
          items: [],
          isSeparator: false,
          solidLiquidValue: vIng.solidLiquid || item.solidLiquid || "",
        };
      }

      if ((vIng.solidLiquid || item.solidLiquid) && !currentGroup.solidLiquidValue) {
        currentGroup.solidLiquidValue = vIng.solidLiquid || item.solidLiquid;
      }
      if (item.isSeparator) {
        currentGroup.isSeparator = true;
      }

      currentGroup.items.push({ item, vIng, index });
    });

    if (currentGroup && currentGroup.items.length > 0) {
      groups.push(currentGroup);
    }

    return groups;
  }, [globalIngredients, vIngredientsByKey, displayIngredients]);

  const versionId =
    effectiveVersionData?._id ||
    vItem?._id ||
    (data?._id && versionNumStr ? `${data._id}_v${versionNumStr}` : null) ||
    (versionNumStr ? `v_${versionNumStr}` : "default");

  const storageKey = `ingredient_table_cols_${versionId}`;

  // Hidden columns state for THIS version - automatically adjusts column width & persists across reload
  const [selectedColumns, setSelectedColumns] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {
      console.warn("Failed to load selected columns from localStorage", e);
    }
    return [];
  });

  const handleSelectedColumnsChange = useCallback(
    (newCols) => {
      setSelectedColumns(newCols);
      try {
        localStorage.setItem(storageKey, JSON.stringify(newCols));
      } catch (e) {
        console.warn("Failed to save selected columns to localStorage", e);
      }
    },
    [storageKey]
  );

  const isPsd = isBeveragePsdRecipe(recipeFormat || data?.recipeType || vItem?.recipeType);
  const versionColumnWidth = (isPsd ? 480 : 400) + selectedColumns.length * 120;

  return (
    <div
      data-version-col={String(vItem?.version ?? "")}
      data-version-num={String(Number(vItem?.version ?? 0) + 1)}
      data-version-id={String(vItem?._id ?? "")}
      id={`version-column-${vItem?.version}`}
      className={cn(
        "flex-none border-r border-[#EEEBF4] dark:border-primary/40 flex flex-col transition-all duration-200",
        isSelectingForCompare && !isSelectedForCompare && "opacity-40 bg-gray-100/50 dark:bg-[#151221]/80 select-none"
      )}
      style={{ width: `${versionColumnWidth}px` }}
    >
      {/* 1. INGREDIENT TABLE SECTION */}
      <IngredientTable
        tableRef={ingredientRef}
        minHeight={ingredientHeight}
        vItem={normalizedVItem}
        data={data}
        versionNumStr={versionNumStr}
        vIsFinalized={vIsFinalized}
        onFinalizeVersion={onFinalizeVersion}
        onFullDownload={onFullDownload}
        onClientDownload={onClientDownload}
        showSolidLiquidColumn={showSolidLiquidColumn}
        ingredientGroups={ingredientGroups}
        vTotals={effectiveTotals}
        onPrepareSample={onPrepareSample}
        onSample={onSample}
        onEditRow={onEditRow}
        formatDate={formatDate}
        isSelectingForCompare={isSelectingForCompare}
        isSelectedForCompare={isSelectedForCompare}
        onToggleSelectCompare={onToggleSelectCompare}
        selectedColumns={selectedColumns}
        onSelectedColumnsChange={handleSelectedColumnsChange}
        onClearVersionIngredients={onClearVersionIngredients}
        onSaveSpecificFields={onSaveSpecificFields}
        recipeFormat={recipeFormat}
        isBeveragePsd={isPsd}
        scaleBatchSize={scaleBatchSize}
      />

      {/* 2. BATCH SUMMARY SECTION */}
      <BatchSummary
        vItem={vItem}
        data={data}
        isFirstVersion={isFirstVersion}
        batchRef={batchRef}
        minHeight={batchHeight}
        vBatchSummary={vBatchSummary}
        draftYield={draftYield}
        setDraftYield={setDraftYield}
        draftServingSize={draftServingSize}
        setDraftServingSize={setDraftServingSize}
        draftPerPiece={draftPerPiece}
        setDraftPerPiece={setDraftPerPiece}
        draftPacketQuantity={draftPacketQuantity}
        setDraftPacketQuantity={setDraftPacketQuantity}
        isBatchSummaryEditing={isBatchSummaryEditing}
        setIsBatchSummaryEditing={setIsBatchSummaryEditing}
        onSaveSpecificFields={onSaveSpecificFields}
        isConfectionary={isConfectionary}
        recipeFormat={recipeFormat}
        isBeveragePsd={isPsd}
        vIsFinalized={vIsFinalized}
        isSelectingForCompare={isSelectingForCompare}
      />

      {/* 3. STANDARD OPERATING PROCEDURE SECTION */}
      <StandardOperatingProcedure
        vItem={vItem}
        data={data}
        normalizedVItem={normalizedVItem}
        recipeFormat={recipeFormat}
        isFirstVersion={isFirstVersion}
        sopRef={sopRef}
        minHeight={sopHeight}
        onSaveSpecificFields={onSaveSpecificFields}
        isConfectionary={isConfectionary}
        formatDate={formatDate}
        vIsFinalized={vIsFinalized}
        isSelectingForCompare={isSelectingForCompare}
      />

      {/* 4. SENSORY FEEDBACK SECTION */}
      <SensoryFeedback
        sensoryRef={sensoryRef}
        minHeight={sensoryHeight}
        vItem={vItem}
        formatDate={formatDate}
      />
    </div>
  );
}

// ================= MAIN RECIPE DETAILS TABLE CONTAINER =================
export default function RecipeDetailsTable({
  data,
  versions = [],
  currentVersion,
  onIngredientsChange,
  onRecipeChange,
  sopData,
  onSOPChange,
  onSaveSpecificFields,
  onFinalizeVersion,
  onFullDownload,
  onClientDownload,
  onPrepareSample,
  onSample,
  formatDate = (d) => d || "-",
  recipeFormat = "bakery",
  isFinalized = false,
  isSelectingForCompare = false,
  isCompareConfirmed = false,
  selectedCompareVersionIds = [],
  onToggleSelectCompareVersion,
  selectedScaleBatchVersionId,
  onSelectScaleBatchVersion,
  tableContainerRef: externalTableContainerRef,
}) {
  const [isBFFModalOpen, setIsBFFModalOpen] = useState(false);
  const [isStandardIngredientModalOpen, setIsStandardIngredientModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [targetVersionForIngredient, setTargetVersionForIngredient] = useState(null);
  const [ingredientToArchive, setIngredientToArchive] = useState(null);
  const [scaleBatchSizesByVersionId, setScaleBatchSizesByVersionId] = useState({});

  const handleScaleBatchSizeChange = useCallback((newSize, vId) => {
    if (!vId) return;
    setScaleBatchSizesByVersionId((prev) => ({
      ...prev,
      [vId]: newSize,
    }));
  }, []);

  const deleteIngredientMutation = useDeleteRecipeIngredient();

  const HIDDEN_TYPE_SOLID_LIQUID_RECIPES = ["beverage", "beverage psd"];
  const recipeTypeLower = data?.recipeType?.toLowerCase() || recipeFormat || "";
  const effectiveRecipeFormat = (recipeFormat && recipeFormat !== "bakery") ? recipeFormat : (recipeTypeLower || recipeFormat || "bakery");
  const isHiddenTypeAndSolidLiquid = HIDDEN_TYPE_SOLID_LIQUID_RECIPES.includes(recipeTypeLower);
  const isConfectionary = isConfectionaryRecipe(data?.recipeType) || isConfectionaryRecipe(recipeFormat);
  const showSolidLiquidColumn = !isHiddenTypeAndSolidLiquid && !isConfectionary;

  const displayVersions = useMemo(() => {
    if (Array.isArray(versions) && versions.length > 0) {
      return [...versions].sort(
        (a, b) => (Number(b.version) || 0) - (Number(a.version) || 0)
      );
    }
    return [
      {
        _id: data?._id,
        version: currentVersion ?? data?.version ?? 0,
        createdAt: data?.createdAt,
        recipeCode: data?.recipeCode || "-",
        recipeStatus: data?.recipeStatus,
        ingredients: data?.ingredients,
        outputYield: data?.outputYield ?? 0,
        outputServingSize: data?.outputServingSize ?? 0,
      },
    ];
  }, [versions, data, currentVersion]);

  const visibleVersions = useMemo(() => {
    if (isCompareConfirmed) {
      return displayVersions.filter((v) =>
        selectedCompareVersionIds.includes(v._id ?? v.version)
      );
    }
    return displayVersions;
  }, [isCompareConfirmed, displayVersions, selectedCompareVersionIds]);

  const scaleBatchVersion = useMemo(() => {
    if (!isCompareConfirmed) return null;
    const found = displayVersions.find(
      (v) => (v._id ?? v.version) === selectedScaleBatchVersionId
    );
    return found || visibleVersions[0] || displayVersions[0] || null;
  }, [isCompareConfirmed, displayVersions, selectedScaleBatchVersionId, visibleVersions]);

  const activeCompareVersion = useMemo(() => {
    if (!isCompareConfirmed) return data;
    return scaleBatchVersion || visibleVersions[0] || data;
  }, [isCompareConfirmed, scaleBatchVersion, visibleVersions, data]);

  const isCurrentDataForGlobal =
    activeCompareVersion?._id === data?._id ||
    (activeCompareVersion?.version !== undefined && data?.version !== undefined && activeCompareVersion?.version === data?.version);

  const { data: fetchedGlobalDetail } = useRecipeById(activeCompareVersion?._id, {
    enabled:
      isCompareConfirmed &&
      !isCurrentDataForGlobal &&
      !!activeCompareVersion?._id &&
      (!Array.isArray(activeCompareVersion?.ingredients) || activeCompareVersion.ingredients.length === 0),
  });

  const effectiveGlobalRecipe = useMemo(() => {
    if (!isCompareConfirmed) return data;
    if (isCurrentDataForGlobal && data) return data;
    if (fetchedGlobalDetail) return { ...activeCompareVersion, ...fetchedGlobalDetail };
    return activeCompareVersion || data;
  }, [isCompareConfirmed, isCurrentDataForGlobal, data, fetchedGlobalDetail, activeCompareVersion]);

  const versionIngredientQueries = useQueries({
    queries: visibleVersions.map((version) => ({
      queryKey: queryKeys.recipes.detail(version?._id),
      queryFn: ({ signal }) => api.get(`/recipes/${version._id}`, { signal }).then((res) => res.data),
      enabled:
        Boolean(version?._id) &&
        version._id !== data?._id &&
        (!Array.isArray(version?.ingredients) || version.ingredients.length === 0),
      select: (responseData) => responseData?.data || responseData || null,
    })),
  });

  const versionIngredients = useMemo(
    () =>
      visibleVersions.map((version, index) =>
        Array.isArray(version?.ingredients) && version.ingredients.length > 0
          ? version.ingredients
          : versionIngredientQueries[index]?.data?.ingredients || []
      ),
    [visibleVersions, versionIngredientQueries]
  );

  const allDistinctIngredients = useMemo(() => {
    const list = [];
    const seenKeys = new Set();

    const addIngredientsFrom = (sourceIngredients = []) => {
      if (!Array.isArray(sourceIngredients)) return;
      sourceIngredients.forEach((item) => {
        if (!item) return;
        const key = getIngredientMatchKey(item);
        if (key && !seenKeys.has(key)) {
          seenKeys.add(key);
          list.push(item);
        }
      });
    };

    addIngredientsFrom(effectiveGlobalRecipe?.ingredients);
    addIngredientsFrom(data?.ingredients);

    versionIngredients.forEach((ingredients) => {
      addIngredientsFrom(ingredients);
    });

    return list;
  }, [effectiveGlobalRecipe?.ingredients, data?.ingredients, versionIngredients]);

  const baseComputed = useMemo(
    () =>
      buildIngredientsDisplayData(
        {
          ...effectiveGlobalRecipe,
          ingredients: allDistinctIngredients,
        },
        { recipeFormat: effectiveRecipeFormat }
      ),
    [effectiveGlobalRecipe, allDistinctIngredients, effectiveRecipeFormat]
  );
  const globalIngredients = baseComputed.ingredients;

  const handleBFFProductConfirm = (formData) => {
    const currentIngredients = Array.isArray(data?.ingredients) ? data.ingredients : [];
    const newIngredient = {
      ...formData,
      order: currentIngredients.length,
      sortOrder: currentIngredients.length,
    };
    onIngredientsChange?.([...currentIngredients, newIngredient], data?._id);
  };

  const handleStandardIngredientConfirm = (formData) => {
    const currentIngredients = Array.isArray(data?.ingredients) ? data.ingredients : [];
    const newIngredient = {
      ...formData,
      order: currentIngredients.length,
      sortOrder: currentIngredients.length,
    };
    onIngredientsChange?.([...currentIngredients, newIngredient], data?._id);
  };

  const handleEditRow = (ingredient, index, vItem) => {
    const targetRecipe = vItem || data;
    const currentIngredients = Array.isArray(targetRecipe?.ingredients) ? targetRecipe.ingredients : [];
    const matchKey = getIngredientMatchKey(ingredient);
    const existingIndex = currentIngredients.findIndex(
      (ing) => getIngredientMatchKey(ing) === matchKey
    );

    setSelectedIngredient({
      ...ingredient,
      index: existingIndex !== -1 ? existingIndex : (ingredient.originalIndex ?? -1),
    });
    setTargetVersionForIngredient(targetRecipe);
    setIsEditModalOpen(true);
  };

  const handleEditConfirm = (formData) => {
    const targetRecipe = targetVersionForIngredient || data;
    const currentIngredients = Array.isArray(targetRecipe?.ingredients) ? [...targetRecipe.ingredients] : [];
    const updateIndex = Number(formData?.index);

    if (Number.isInteger(updateIndex) && updateIndex >= 0 && updateIndex < currentIngredients.length) {
      currentIngredients[updateIndex] = {
        ...currentIngredients[updateIndex],
        ...formData,
        order: currentIngredients[updateIndex]?.order ?? updateIndex,
        sortOrder: currentIngredients[updateIndex]?.sortOrder ?? updateIndex,
      };
    } else {
      const matchKey = getIngredientMatchKey(formData);
      const existingIdx = currentIngredients.findIndex(
        (ing) => getIngredientMatchKey(ing) === matchKey
      );
      if (existingIdx !== -1) {
        currentIngredients[existingIdx] = {
          ...currentIngredients[existingIdx],
          ...formData,
          order: currentIngredients[existingIdx]?.order ?? existingIdx,
          sortOrder: currentIngredients[existingIdx]?.sortOrder ?? existingIdx,
        };
      } else {
        currentIngredients.push({
          ...formData,
          order: currentIngredients.length,
          sortOrder: currentIngredients.length,
        });
      }
    }

    onIngredientsChange?.(currentIngredients, targetRecipe?._id);
    setIsEditModalOpen(false);
    setTargetVersionForIngredient(null);
  };

  const handleDeleteIngredient = async (ingredient) => {
    if (!ingredient) return;
    const targetRecipe = data;
    const currentIngredients = Array.isArray(targetRecipe?.ingredients) ? [...targetRecipe.ingredients] : [];

    let deleteIndex = currentIngredients.findIndex((ing) => isIngredientMatch(ing, ingredient));
    if (deleteIndex === -1 && ingredient.originalIndex !== undefined && ingredient.originalIndex >= 0 && ingredient.originalIndex < currentIngredients.length) {
      deleteIndex = ingredient.originalIndex;
    }

    if (deleteIndex !== -1) {
      currentIngredients.splice(deleteIndex, 1);
      const reindexed = currentIngredients.map((item, idx) => ({
        ...item,
        sl: idx + 1,
        order: idx,
        sortOrder: idx,
      }));
      await onIngredientsChange?.(reindexed, targetRecipe?._id);
    }
  };

  const handleClearVersionIngredients = async (targetVItem) => {
    const targetRecipe = targetVItem || data;
    const targetId = targetRecipe?._id;
    if (!targetId) return;

    const sourceIngredients =
      Array.isArray(targetRecipe?.ingredients) && targetRecipe.ingredients.length > 0
        ? targetRecipe.ingredients
        : Array.isArray(data?.ingredients) && data.ingredients.length > 0
          ? data.ingredients
          : globalIngredients;

    const clearedIngredients = sourceIngredients.map((item) => ({
      ...item,
      quantity: 0,
      clientRate: 0,
      clientRateAtCreation: 0,
      bffRate: 0,
      bffRateAtCreation: 0,
    }));

    await onIngredientsChange?.(clearedIngredients, targetId);
  };

  const handleArchiveRow = (ingredient, vItem) => {
    setIngredientToArchive(ingredient);
    setTargetVersionForIngredient(vItem || data);
    setIsArchiveModalOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!ingredientToArchive) return;
    const targetRecipe = targetVersionForIngredient || data;
    const currentIngredients = Array.isArray(targetRecipe?.ingredients) ? [...targetRecipe.ingredients] : [];

    let deleteIndex = currentIngredients.findIndex((ing) => isIngredientMatch(ing, ingredientToArchive));
    if (deleteIndex === -1 && ingredientToArchive.originalIndex !== undefined && ingredientToArchive.originalIndex >= 0 && ingredientToArchive.originalIndex < currentIngredients.length) {
      deleteIndex = ingredientToArchive.originalIndex;
    }

    if (deleteIndex !== -1) {
      currentIngredients.splice(deleteIndex, 1);
      const reindexed = currentIngredients.map((item, idx) => ({
        ...item,
        sl: idx + 1,
        order: idx,
        sortOrder: idx,
      }));
      await onIngredientsChange?.(reindexed, targetRecipe?._id);
    }
    setIsArchiveModalOpen(false);
    setIngredientToArchive(null);
    setTargetVersionForIngredient(null);
  };

  // Section Height Matching for perfect horizontal alignment with left column & across all visible versions
  const ingredientElementsRef = useRef(new Map());
  const batchElementsRef = useRef(new Map());
  const sopElementsRef = useRef(new Map());
  const sensoryElementsRef = useRef(new Map());
  const resizeObserverRef = useRef(null);
  const [sectionHeights, setSectionHeights] = useState({
    ingredientTable: null,
    batchSummary: null,
    sop: null,
    sensoryFeedback: null,
  });

  const updateHeights = useCallback(() => {
    let maxIng = 0;
    ingredientElementsRef.current.forEach((el) => {
      if (el) {
        const h = el.offsetHeight;
        if (h > maxIng) maxIng = h;
      }
    });

    let maxBS = 0;
    batchElementsRef.current.forEach((el) => {
      if (el) {
        const h = el.offsetHeight;
        if (h > maxBS) maxBS = h;
      }
    });

    let maxSOP = 0;
    sopElementsRef.current.forEach((el) => {
      if (el) {
        const h = el.offsetHeight;
        if (h > maxSOP) maxSOP = h;
      }
    });

    let maxSensory = 0;
    sensoryElementsRef.current.forEach((el) => {
      if (el) {
        const h = el.offsetHeight;
        if (h > maxSensory) maxSensory = h;
      }
    });

    setSectionHeights((prev) => {
      const newIng = maxIng || prev.ingredientTable || null;
      const newBS = maxBS || prev.batchSummary || null;
      const newSOP = maxSOP || prev.sop || null;
      const newSensory = maxSensory || prev.sensoryFeedback || null;
      if (
        prev.ingredientTable === newIng &&
        prev.batchSummary === newBS &&
        prev.sop === newSOP &&
        prev.sensoryFeedback === newSensory
      ) {
        return prev;
      }
      return {
        ingredientTable: newIng,
        batchSummary: newBS,
        sop: newSOP,
        sensoryFeedback: newSensory,
      };
    });
  }, []);

  useEffect(() => {
    resizeObserverRef.current = new ResizeObserver(() => {
      updateHeights();
    });
    ingredientElementsRef.current.forEach((node) => {
      if (node && resizeObserverRef.current) {
        resizeObserverRef.current.observe(node);
      }
    });
    batchElementsRef.current.forEach((node) => {
      if (node && resizeObserverRef.current) {
        resizeObserverRef.current.observe(node);
      }
    });
    sopElementsRef.current.forEach((node) => {
      if (node && resizeObserverRef.current) {
        resizeObserverRef.current.observe(node);
      }
    });
    sensoryElementsRef.current.forEach((node) => {
      if (node && resizeObserverRef.current) {
        resizeObserverRef.current.observe(node);
      }
    });

    updateHeights();
    const t1 = setTimeout(updateHeights, 50);
    const t2 = setTimeout(updateHeights, 150);
    const t3 = setTimeout(updateHeights, 500);
    window.addEventListener("resize", updateHeights);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      window.removeEventListener("resize", updateHeights);
    };
  }, [updateHeights, visibleVersions]);

  const ingredientRefCallbacksRef = useRef(new Map());
  const batchRefCallbacksRef = useRef(new Map());
  const sopRefCallbacksRef = useRef(new Map());
  const sensoryRefCallbacksRef = useRef(new Map());

  const getIngredientRefCallback = useCallback(
    (key) => {
      if (!ingredientRefCallbacksRef.current.has(key)) {
        ingredientRefCallbacksRef.current.set(key, (node) => {
          if (node) {
            ingredientElementsRef.current.set(key, node);
            if (resizeObserverRef.current) {
              resizeObserverRef.current.observe(node);
            }
            updateHeights();
          } else {
            const existing = ingredientElementsRef.current.get(key);
            if (existing && resizeObserverRef.current) {
              resizeObserverRef.current.unobserve(existing);
            }
            ingredientElementsRef.current.delete(key);
            updateHeights();
          }
        });
      }
      return ingredientRefCallbacksRef.current.get(key);
    },
    [updateHeights]
  );

  const getBatchRefCallback = useCallback(
    (key) => {
      if (!batchRefCallbacksRef.current.has(key)) {
        batchRefCallbacksRef.current.set(key, (node) => {
          if (node) {
            batchElementsRef.current.set(key, node);
            if (resizeObserverRef.current) {
              resizeObserverRef.current.observe(node);
            }
            updateHeights();
          } else {
            const existing = batchElementsRef.current.get(key);
            if (existing && resizeObserverRef.current) {
              resizeObserverRef.current.unobserve(existing);
            }
            batchElementsRef.current.delete(key);
            updateHeights();
          }
        });
      }
      return batchRefCallbacksRef.current.get(key);
    },
    [updateHeights]
  );

  const getSopRefCallback = useCallback(
    (key) => {
      if (!sopRefCallbacksRef.current.has(key)) {
        sopRefCallbacksRef.current.set(key, (node) => {
          if (node) {
            sopElementsRef.current.set(key, node);
            if (resizeObserverRef.current) {
              resizeObserverRef.current.observe(node);
            }
            updateHeights();
          } else {
            const existing = sopElementsRef.current.get(key);
            if (existing && resizeObserverRef.current) {
              resizeObserverRef.current.unobserve(existing);
            }
            sopElementsRef.current.delete(key);
            updateHeights();
          }
        });
      }
      return sopRefCallbacksRef.current.get(key);
    },
    [updateHeights]
  );

  const getSensoryRefCallback = useCallback(
    (key) => {
      if (!sensoryRefCallbacksRef.current.has(key)) {
        sensoryRefCallbacksRef.current.set(key, (node) => {
          if (node) {
            sensoryElementsRef.current.set(key, node);
            if (resizeObserverRef.current) {
              resizeObserverRef.current.observe(node);
            }
            updateHeights();
          } else {
            const existing = sensoryElementsRef.current.get(key);
            if (existing && resizeObserverRef.current) {
              resizeObserverRef.current.unobserve(existing);
            }
            sensoryElementsRef.current.delete(key);
            updateHeights();
          }
        });
      }
      return sensoryRefCallbacksRef.current.get(key);
    },
    [updateHeights]
  );

  // Sync scroll for the static bottom scrollbar
  const localTableContainerRef = useRef(null);
  const tableContainerRef = externalTableContainerRef || localTableContainerRef;
  const scrollbarTrackRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [clientWidth, setClientWidth] = useState(0);

  const updateScrollDimensions = React.useCallback(() => {
    if (tableContainerRef.current) {
      setScrollWidth(tableContainerRef.current.scrollWidth);
      setClientWidth(tableContainerRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    updateScrollDimensions();
    const timer = setTimeout(updateScrollDimensions, 100);
    const interval = setInterval(updateScrollDimensions, 1000);
    window.addEventListener("resize", updateScrollDimensions);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
      window.removeEventListener("resize", updateScrollDimensions);
    };
  }, [displayVersions, globalIngredients, updateScrollDimensions]);

  const isSyncingScrollRef = useRef(false);

  const handleTableScroll = (e) => {
    if (isSyncingScrollRef.current) return;
    if (scrollbarTrackRef.current && e.target) {
      isSyncingScrollRef.current = true;
      scrollbarTrackRef.current.scrollLeft = e.target.scrollLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleBottomScroll = (e) => {
    if (isSyncingScrollRef.current) return;
    if (tableContainerRef.current && e.target) {
      isSyncingScrollRef.current = true;
      tableContainerRef.current.scrollLeft = e.target.scrollLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const hasHorizontalScroll = scrollWidth > clientWidth + 10;
  const leftColWidth = isConfectionary ? 420 : 340;

  // Drag-to-scroll functionality
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragInfoRef = useRef({
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    startScrollTop: 0,
    hasDragged: false,
    lastX: 0,
    lastY: 0,
    lastTime: 0,
    velocityX: 0,
    velocityY: 0,
    vContainer: null,
  });
  const momentumAnimIdRef = useRef(null);

  const stopMomentum = useCallback(() => {
    if (momentumAnimIdRef.current) {
      cancelAnimationFrame(momentumAnimIdRef.current);
      momentumAnimIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopMomentum();
    };
  }, [stopMomentum]);

  const getVerticalScrollParent = useCallback((node) => {
    if (!node) return window;
    let current = node.parentElement;
    while (current) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY;
      if (
        (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
        current.scrollHeight > current.clientHeight
      ) {
        return current;
      }
      current = current.parentElement;
    }
    return window;
  }, []);

  const handleMouseDown = useCallback(
    (e) => {
      // Only primary mouse button (left click)
      if (e.button !== 0) return;
      const container = tableContainerRef.current;
      if (!container) return;

      stopMomentum();

      // Check if clicked element is an interactive input or control where dragging is not desired
      const target = e.target;
      if (target.closest("input, textarea, select, [contenteditable='true']")) {
        return;
      }

      const vContainer = getVerticalScrollParent(container);
      const startScrollTop =
        vContainer === window ? (window.scrollY || window.pageYOffset || 0) : vContainer.scrollTop;

      setIsMouseDown(true);

      dragInfoRef.current = {
        startX: e.pageX,
        startY: e.pageY,
        startScrollLeft: container.scrollLeft,
        startScrollTop,
        hasDragged: false,
        lastX: e.pageX,
        lastY: e.pageY,
        lastTime: performance.now(),
        velocityX: 0,
        velocityY: 0,
        vContainer,
      };

      const handleMouseMove = (moveEvent) => {
        const dx = moveEvent.pageX - dragInfoRef.current.startX;
        const dy = moveEvent.pageY - dragInfoRef.current.startY;
        const now = performance.now();
        const dt = now - dragInfoRef.current.lastTime;

        if (!dragInfoRef.current.hasDragged && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
          dragInfoRef.current.hasDragged = true;
          setIsDragging(true);
        }

        if (dragInfoRef.current.hasDragged) {
          moveEvent.preventDefault();
          container.scrollLeft = dragInfoRef.current.startScrollLeft - dx;

          const targetVContainer = dragInfoRef.current.vContainer;
          if (targetVContainer === window) {
            window.scrollTo(window.scrollX, dragInfoRef.current.startScrollTop - dy);
          } else if (targetVContainer) {
            targetVContainer.scrollTop = dragInfoRef.current.startScrollTop - dy;
          }

          if (dt > 0) {
            const instantVelocityX = (moveEvent.pageX - dragInfoRef.current.lastX) / dt;
            const instantVelocityY = (moveEvent.pageY - dragInfoRef.current.lastY) / dt;
            dragInfoRef.current.velocityX =
              0.8 * instantVelocityX + 0.2 * (dragInfoRef.current.velocityX || 0);
            dragInfoRef.current.velocityY =
              0.8 * instantVelocityY + 0.2 * (dragInfoRef.current.velocityY || 0);
            dragInfoRef.current.lastX = moveEvent.pageX;
            dragInfoRef.current.lastY = moveEvent.pageY;
            dragInfoRef.current.lastTime = now;
          }
        }
      };

      const handleMouseUp = () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);

        const hadDragged = dragInfoRef.current.hasDragged;
        const timeSinceLastMove = performance.now() - dragInfoRef.current.lastTime;
        let vx = timeSinceLastMove > 80 ? 0 : dragInfoRef.current.velocityX;
        let vy = timeSinceLastMove > 80 ? 0 : dragInfoRef.current.velocityY;

        if (Math.abs(vx) > 0.15 || Math.abs(vy) > 0.15) {
          if (vx > 2.5) vx = 2.5;
          if (vx < -2.5) vx = -2.5;
          if (vy > 2.5) vy = 2.5;
          if (vy < -2.5) vy = -2.5;

          let currentVx = vx;
          let currentVy = vy;
          const step = () => {
            if ((Math.abs(currentVx) < 0.05 && Math.abs(currentVy) < 0.05) || !tableContainerRef.current) {
              momentumAnimIdRef.current = null;
              return;
            }
            if (Math.abs(currentVx) >= 0.05) {
              tableContainerRef.current.scrollLeft -= currentVx * 14;
              currentVx *= 0.94;
            }
            if (Math.abs(currentVy) >= 0.05) {
              const targetVContainer = dragInfoRef.current.vContainer;
              if (targetVContainer === window) {
                window.scrollBy(0, -currentVy * 14);
              } else if (targetVContainer) {
                targetVContainer.scrollTop -= currentVy * 14;
              }
              currentVy *= 0.94;
            }
            momentumAnimIdRef.current = requestAnimationFrame(step);
          };
          momentumAnimIdRef.current = requestAnimationFrame(step);
        }

        requestAnimationFrame(() => {
          setIsMouseDown(false);
          setIsDragging(false);
          if (hadDragged) {
            setTimeout(() => {
              dragInfoRef.current.hasDragged = false;
            }, 60);
          } else {
            dragInfoRef.current.hasDragged = false;
          }
        });
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [tableContainerRef, stopMomentum, getVerticalScrollParent]
  );

  const handleClickCapture = useCallback((e) => {
    if (dragInfoRef.current.hasDragged) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  // Auto-scroll selected version column smoothly into view if outside visible table area
  useEffect(() => {
    if (currentVersion === undefined || currentVersion === null) return;
    const container = tableContainerRef.current;
    if (!container) return;

    let isCancelled = false;

    const performScroll = (behavior = "smooth") => {
      if (isCancelled) return false;
      const targetCol =
        container.querySelector(`[data-version-col="${currentVersion}"]`) ||
        container.querySelector(`[data-version-num="${Number(currentVersion) + 1}"]`) ||
        container.querySelector(`[data-version-num="${currentVersion}"]`) ||
        container.querySelector(`[data-version-id="${currentVersion}"]`) ||
        container.querySelector(`#version-column-${currentVersion}`);

      if (!targetCol) return false;

      const stickyLeft = container.querySelector("[data-table-sticky-left]");
      const stickyWidth = stickyLeft
        ? (stickyLeft.getBoundingClientRect().width || stickyLeft.offsetWidth || leftColWidth)
        : leftColWidth;

      const targetScrollLeft = Math.max(0, targetCol.offsetLeft - stickyWidth);
      const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
      const finalScrollLeft = Math.min(targetScrollLeft, maxScroll);

      if (Math.abs(container.scrollLeft - finalScrollLeft) <= 3) return true;

      try {
        container.scrollTo({
          left: finalScrollLeft,
          behavior,
        });
      } catch {
        container.scrollLeft = finalScrollLeft;
      }

      setTimeout(() => {
        if (!isCancelled && container && Math.abs(container.scrollLeft - finalScrollLeft) > 10) {
          container.scrollLeft = finalScrollLeft;
        }
      }, 300);

      return true;
    };

    const rafId = requestAnimationFrame(() => performScroll("smooth"));
    const t1 = setTimeout(() => performScroll("smooth"), 60);
    const t2 = setTimeout(() => performScroll("smooth"), 180);

    return () => {
      isCancelled = true;
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [currentVersion, leftColWidth, tableContainerRef, visibleVersions]);

  return (
    <div className="flex flex-col w-full relative">
      {/* Unified Scrollable Table & Sections Container */}
      <div
        ref={tableContainerRef}
        onScroll={handleTableScroll}
        onMouseDown={handleMouseDown}
        onClickCapture={handleClickCapture}
        className={cn(
          "overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden w-full border border-[#EEEBF4] dark:border-primary/40 rounded-3xl lg:rounded-xl xl:rounded-2xl 2xl:rounded-2xl 3xl:rounded-3xl bg-white dark:bg-[#0D0B14]",
          isMouseDown && "cursor-grabbing select-none [&_*]:cursor-grabbing!"
        )}
      >
        <div className="flex min-w-max">
          {/* ================= LEFT CONTINUOUS SOLID FIXED COLUMN ================= */}
          <div
            data-table-sticky-left
            className={cn(
              "sticky left-0 z-20 bg-white dark:bg-[#0D0B14] border-r border-[#EEEBF4] dark:border-primary/40 flex-none shadow-[4px_0_10px_rgba(0,0,0,0.02)] flex flex-col",
              isConfectionary
                ? "w-[420px] lg:w-[225px] xl:w-[285px] 2xl:w-[340px] 3xl:w-[420px]"
                : "w-[340px] lg:w-[180px] xl:w-[230px] 2xl:w-[275px] 3xl:w-[340px]"
            )}
          >
            {/* 1. Ingredient Table Left Header */}
            <IngredientTableLeftHeader
              height={sectionHeights.ingredientTable}
              globalIngredients={globalIngredients}
              isConfectionary={isConfectionary}
              isBeveragePsd={isBeveragePsdRecipe(effectiveRecipeFormat || data?.recipeType)}
              recipeFormat={effectiveRecipeFormat}
              data={data}
              onOpenBFFModal={() => setIsBFFModalOpen(true)}
              onOpenStandardIngredientModal={() => setIsStandardIngredientModalOpen(true)}
              isSelectingForCompare={isSelectingForCompare}
              onDeleteIngredient={handleDeleteIngredient}
              onReorderIngredients={(reorderedList) => onIngredientsChange?.(reorderedList, data?._id)}
              isFinalized={isFinalized}
              isLoading={deleteIngredientMutation.isPending}
            />

            {/* 2. Batch Summary Left Title */}
            <BatchSummaryLeftHeader height={sectionHeights.batchSummary} />

            {/* 3. Standard Operating Procedure Left Title */}
            <StandardOperatingProcedureLeftHeader height={sectionHeights.sop} />

            {/* 4. Sensory Feedback Left Title */}
            <SensoryFeedbackLeftHeader height={sectionHeights.sensoryFeedback} />
          </div>

          {/* ================= RIGHT SCROLLABLE VERSION COLUMNS ================= */}
          <div className="flex items-start">
            {visibleVersions.map((vItem, vIndex) => {
              const vKey = vItem._id || vItem.version || vIndex;
              const vId = vItem._id ?? vItem.version;
              return (
                <VersionColumn
                  key={vKey}
                  vItem={vItem}
                  data={data}
                  isFirstVersion={vIndex === 0}
                  ingredientRef={getIngredientRefCallback(vKey)}
                  ingredientHeight={sectionHeights.ingredientTable}
                  batchRef={getBatchRefCallback(vKey)}
                  sopRef={getSopRefCallback(vKey)}
                  sensoryRef={getSensoryRefCallback(vKey)}
                  batchHeight={sectionHeights.batchSummary}
                  sopHeight={sectionHeights.sop}
                  sensoryHeight={sectionHeights.sensoryFeedback}
                  globalIngredients={globalIngredients}
                  showSolidLiquidColumn={showSolidLiquidColumn}
                  formatDate={formatDate}
                  onFinalizeVersion={onFinalizeVersion}
                  onFullDownload={onFullDownload}
                  onClientDownload={onClientDownload}
                  onPrepareSample={onPrepareSample}
                  onSample={(v, r) => onSample?.(v, r, scaleBatchSizesByVersionId[vId])}
                  onEditRow={handleEditRow}
                  onClearVersionIngredients={handleClearVersionIngredients}
                  onSaveSpecificFields={onSaveSpecificFields}
                  recipeFormat={effectiveRecipeFormat}
                  isConfectionary={isConfectionary}
                  isFinalized={isFinalized}
                  isSelectingForCompare={isSelectingForCompare}
                  isSelectedForCompare={selectedCompareVersionIds.includes(vItem._id ?? vItem.version)}
                  onToggleSelectCompare={() => onToggleSelectCompareVersion?.(vItem)}
                  scaleBatchSize={scaleBatchSizesByVersionId[vId]}
                />
              );
            })}

            {/* Far-Right Scale Batch Column in Confirmed Comparison Mode */}
            {isCompareConfirmed && scaleBatchVersion && (
              <ScaleBatchColumn
                scaleBatchVersion={scaleBatchVersion}
                comparedVersions={visibleVersions}
                onSelectScaleBatchVersion={onSelectScaleBatchVersion}
                scaleBatchSize={scaleBatchSizesByVersionId[scaleBatchVersion?._id ?? scaleBatchVersion?.version]}
                onScaleBatchSizeChange={(newSize) => {
                  const targetVId = scaleBatchVersion?._id ?? scaleBatchVersion?.version;
                  handleScaleBatchSizeChange(newSize, targetVId);
                }}
                data={data}
                globalIngredients={globalIngredients}
                showSolidLiquidColumn={showSolidLiquidColumn}
                formatDate={formatDate}
                isConfectionary={isConfectionary}
                recipeFormat={effectiveRecipeFormat}
                isBeveragePsd={isBeveragePsdRecipe(effectiveRecipeFormat || data?.recipeType)}
                isMobile={false}
                tableRef={getIngredientRefCallback("scale_batch")}
                minHeight={sectionHeights.ingredientTable}
                batchRef={getBatchRefCallback("scale_batch")}
                sopRef={getSopRefCallback("scale_batch")}
                batchHeight={sectionHeights.batchSummary}
                sopHeight={sectionHeights.sop}
                onSaveSpecificFields={onSaveSpecificFields}
              />
            )}
          </div>
        </div>
      </div>

      {/* Static / Sticky Horizontal Scrollbar Pinned at Bottom of Viewport */}
      {hasHorizontalScroll && (
        <div className="sticky bottom-0 z-40 w-full bg-white/95 dark:bg-[#0D0B14]/95 backdrop-blur-md border-t border-[#EEEBF4] dark:border-primary/40 py-2.5 lg:py-1 xl:py-1.5 2xl:py-2 3xl:py-2.5 px-4 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-4 flex items-center shadow-[0_-4px_12px_rgba(0,0,0,0.04)] rounded-b-3xl lg:rounded-b-xl xl:rounded-b-2xl 2xl:rounded-b-2xl 3xl:rounded-b-3xl">
          {/* Left fixed column offset so the scrollbar track starts after the left column */}
          <div
            className={cn(
              "flex-none hidden md:block",
              isConfectionary
                ? "w-[420px] lg:w-[225px] xl:w-[285px] 2xl:w-[340px] 3xl:w-[420px]"
                : "w-[340px] lg:w-[180px] xl:w-[230px] 2xl:w-[275px] 3xl:w-[340px]"
            )}
          />
          <div
            ref={scrollbarTrackRef}
            onScroll={handleBottomScroll}
            className="flex-1 overflow-x-auto custom-scrollbar h-3 cursor-pointer"
          >
            <div
              style={{ width: `${Math.max(scrollWidth - leftColWidth, 100)}px` }}
              className="h-1"
            />
          </div>
        </div>
      )}

      {/* Modals for Ingredients */}
      <AddBFFProductModal
        isOpen={isBFFModalOpen}
        onClose={() => setIsBFFModalOpen(false)}
        onConfirm={handleBFFProductConfirm}
        isConfectionary={isConfectionary}
      />

      <AddStandardIngredientModal
        isOpen={isStandardIngredientModalOpen}
        onClose={() => setIsStandardIngredientModalOpen(false)}
        onConfirm={handleStandardIngredientConfirm}
        isConfectionary={isConfectionary}
      />

      <EditIngredientModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setTargetVersionForIngredient(null);
        }}
        onConfirm={handleEditConfirm}
        initialData={selectedIngredient}
        isConfectionary={isConfectionary}
      />

      <ArchiveIngredientModal
        open={isArchiveModalOpen}
        onOpenChange={(open) => {
          setIsArchiveModalOpen(open);
          if (!open) setTargetVersionForIngredient(null);
        }}
        ingredientName={ingredientToArchive?.name}
        onConfirm={handleArchiveConfirm}
        isLoading={deleteIngredientMutation.isPending}
      />
    </div>
  );
}
